import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Button, Container, Modal, Row, Col, Card } from "react-bootstrap";
import { api } from "../api";

interface AdminProps {
  token: string | null;
  setToken: (token: string | null) => void;
}

interface Booking {
  id: number;
  name: string;
  phone_number: string;
  email: string;
  address: string;
  details: string;

  // NEW: epoch seconds (UTC) for start/end datetime
  start_date: string | number;
  end_date: string | number;

  status: string | number;
  event_type?: string;
  guest_size?: number;
  action_by?: string | null;
  action_date?: number | string | null; // can be seconds or ms (we handle both)
}

const Admin: React.FC<AdminProps> = ({ token, setToken }) => {
  const navigate = useNavigate();

  const [bookings, setBookings] = useState<Booking[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string>("10");

  const [showModal, setShowModal] = useState(false);
  const [modalAction, setModalAction] = useState<{ bookingId: number; status: string } | null>(
    null
  );

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [currentDetails, setCurrentDetails] = useState<string>("");

  const authHeaders = useMemo(() => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [token]);

  const handleLogout = async () => {
    setToken(null);
    try {
      await api.post("/auth/logout/");
    } catch {
      // ignore
    }
    navigate("/admin/login");
  };

  const fetchBookings = async (status: string = "") => {
    const params = status ? { status } : {};

    try {
      const response = await api.get<Booking[]>("/bookings", {
        headers: authHeaders,
        params,
      });
      setBookings(response.data);
    } catch (error: any) {
      console.error("Error fetching bookings:", error);
      if (error?.response?.status === 401) {
        await handleLogout();
      }
    }
  };

  useEffect(() => {
    setSelectedStatus("10");
    fetchBookings("10");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const getStatusLabel = (status: string) => {
    switch (String(status)) {
      case "10":
        return "Pending";
      case "20":
        return "Accepted";
      case "30":
        return "Declined";
      default:
        return "Unknown";
    }
  };

  const handleStatusUpdate = async (bookingId: number, newStatus: string) => {
    try {
      await api.post(
        `/bookings/${bookingId}/update-status/`,
        { status: newStatus },
        { headers: authHeaders }
      );
      await fetchBookings(selectedStatus);
    } catch (error: any) {
      console.error("Error updating status:", error);
      if (error?.response?.status === 401) {
        await handleLogout();
      }
    }
  };

  // Supports seconds OR milliseconds just in case older data exists
  const normalizeToSeconds = (epoch: number) => {
    return epoch > 10_000_000_000 ? Math.floor(epoch / 1000) : epoch;
  };

  const formatDateTime = (epoch?: number | string | null) => {
    if (!epoch) return "-";
    const raw = Number(epoch);
    if (!Number.isFinite(raw)) return "-";

    const sec = normalizeToSeconds(raw);

    return new Date(sec * 1000).toLocaleString("en-IE", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  // ✅ One line: "18 Jan 2026, 18:00–21:00"
  const formatWhen = (startEpoch?: number | string | null, endEpoch?: number | string | null) => {
    if (!startEpoch || !endEpoch) return "-";

    const sRaw = Number(startEpoch);
    const eRaw = Number(endEpoch);
    if (!Number.isFinite(sRaw) || !Number.isFinite(eRaw)) return "-";

    const sSec = normalizeToSeconds(sRaw);
    const eSec = normalizeToSeconds(eRaw);

    const start = new Date(sSec * 1000);
    const end = new Date(eSec * 1000);

    const datePart = start.toLocaleDateString("en-IE", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });

    const startTimePart = start.toLocaleTimeString("en-IE", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    const endTimePart = end.toLocaleTimeString("en-IE", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    return `${datePart}, ${startTimePart} – ${endTimePart}`;
  };

  const truncateText = (text: string, maxLength: number) => {
    if (!text) return "";
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  const statusHeader = getStatusLabel(selectedStatus);

  if (!token) {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <Container className="my-5">
      <h1 className="text-center mb-4">Admin Dashboard - {statusHeader}</h1>

      {/* Status Filter Buttons */}
      <Row className="justify-content-center mb-4">
        <Col xs="auto">
          <Button
            variant="secondary"
            onClick={() => {
              setSelectedStatus("10");
              fetchBookings("10");
            }}
          >
            Pending
          </Button>
        </Col>
        <Col xs="auto">
          <Button
            variant="success"
            onClick={() => {
              setSelectedStatus("20");
              fetchBookings("20");
            }}
          >
            Accepted
          </Button>
        </Col>
        <Col xs="auto">
          <Button
            variant="danger"
            onClick={() => {
              setSelectedStatus("30");
              fetchBookings("30");
            }}
          >
            Declined
          </Button>
        </Col>
      </Row>

      {/* Booking Cards */}
      <Row xs={1} md={2} lg={3} className="g-4">
        {bookings.length ? (
          bookings.map((booking) => (
            <Col key={booking.id}>
              <Card className="h-100 shadow-sm">
                <Card.Body>
                  <Card.Text>
                    <strong>Name:</strong> {booking.name}
                  </Card.Text>
                  <Card.Text>
                    <strong>Number:</strong> {booking.phone_number}
                  </Card.Text>
                  <Card.Text>
                    <strong>Email:</strong> {booking.email}
                  </Card.Text>
                  <Card.Text>
                    <strong>Address:</strong> {booking.address}
                  </Card.Text>

                  <Card.Text>
                    <strong>Details:</strong> {truncateText(booking.details, 30)}{" "}
                    {booking.details?.length > 30 && (
                      <Button
                        variant="info"
                        size="sm"
                        onClick={() => {
                          setCurrentDetails(booking.details);
                          setShowDetailsModal(true);
                        }}
                      >
                        Info
                      </Button>
                    )}
                  </Card.Text>

                  {/* ✅ One-line date + time */}
                  <Card.Text>
                    <strong>Date:</strong> {formatWhen(booking.start_date, booking.end_date)}
                  </Card.Text>

                  <Card.Text>
                    <strong>Event Type:</strong> {booking.event_type || "-"}
                  </Card.Text>
                  <Card.Text>
                    <strong>Guests:</strong> {booking.guest_size ?? "-"}
                  </Card.Text>

                  {String(booking.status) === "10" && (
                    <div className="d-flex justify-content-between">
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => {
                          setModalAction({ bookingId: booking.id, status: "20" });
                          setShowModal(true);
                        }}
                      >
                        Accept
                      </Button>

                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => {
                          setModalAction({ bookingId: booking.id, status: "30" });
                          setShowModal(true);
                        }}
                      >
                        Decline
                      </Button>
                    </div>
                  )}

                  {String(booking.status) === "20" && (
                    <>
                      <br />
                      <Card.Text>
                        <strong>Person Accepted:</strong> {booking.action_by || "-"}
                      </Card.Text>
                      <Card.Text>
                        <strong>Accepted Date:</strong> {formatDateTime(booking.action_date)}
                      </Card.Text>
                    </>
                  )}

                  {String(booking.status) === "30" && (
                    <>
                      <br />
                      <Card.Text>
                        <strong>Person Declined:</strong> {booking.action_by || "-"}
                      </Card.Text>
                      <Card.Text>
                        <strong>Declined Date:</strong> {formatDateTime(booking.action_date)}
                      </Card.Text>
                    </>
                  )}
                </Card.Body>
              </Card>
            </Col>
          ))
        ) : (
          <p className="text-center">No bookings found.</p>
        )}
      </Row>

      <div className="text-center mt-4">
        <Button variant="danger" onClick={handleLogout}>
          Logout
        </Button>
      </div>

      {/* Confirmation Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Action</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to{" "}
          <strong>{modalAction?.status === "20" ? "Accept" : "Decline"}</strong> this booking?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            No
          </Button>
          <Button
            variant={modalAction?.status === "20" ? "success" : "danger"}
            onClick={() => {
              if (modalAction) {
                handleStatusUpdate(modalAction.bookingId, modalAction.status);
              }
              setShowModal(false);
            }}
          >
            Yes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Details Info Modal */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Booking Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ whiteSpace: "pre-wrap" }}>{currentDetails}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Admin;
